import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

export interface RelationshipUser {
  id: string;
  visibilityId?: string;  // Actual user ID for access grants (undefined for pending)
  name: string;
  email: string;
  status?: 'active' | 'pending';
}

export interface RelationshipsResponse {
  successors: RelationshipUser[];
  keyholders: RelationshipUser[];
}

class RelationshipsService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();
    
    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  async getRelationships(): Promise<RelationshipsResponse> {
    const response = await this.makeRequest('/api/relationships');

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch relationships: ${response.status}`);
    }

    const result = await response.json();

    // Handle the backend response format: { success: true, relationships: {...} }
    const data = result.success && result.relationships ? result.relationships : result;

    // Check if we have the new combined format (successors/keyholders directly)
    if (data && data.successors !== undefined && data.keyholders !== undefined) {
      // New format - already properly structured
      return {
        successors: data.successors || [],
        keyholders: data.keyholders || [],
      };
    }

    // Fallback to old format for backwards compatibility
    const relationshipsArray = Array.isArray(data) ? data : (data?.relationships || []);

    const successors = relationshipsArray
      ?.filter((rel: any) => rel.role === 'successor')
      ?.map((rel: any) => ({
        id: rel.id,
        name: `${rel.secondaryUser?.firstName || ''} ${rel.secondaryUser?.lastName || ''}`.trim() || rel.secondaryUser?.email || 'Unknown',
        email: rel.secondaryUser?.email || '',
        status: rel.status === 'pending' ? 'pending' : 'active',
      })) || [];

    const keyholders = relationshipsArray
      ?.filter((rel: any) => rel.role === 'keyholder')
      ?.map((rel: any) => ({
        id: rel.id,
        name: `${rel.secondaryUser?.firstName || ''} ${rel.secondaryUser?.lastName || ''}`.trim() || rel.secondaryUser?.email || 'Unknown',
        email: rel.secondaryUser?.email || '',
        status: rel.status === 'pending' ? 'pending' : 'active',
      })) || [];

    return {
      successors,
      keyholders
    };
  }
}

export const relationshipsService = new RelationshipsService();